import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Helmet } from 'react-helmet-async';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { useProducts } from './useProducts';

import { useSiteSettings } from './SiteSettingsContext';

import { findProductInCatalog } from './catalog/findProduct';

import { productGroupKey, shopCategoryHubPath, shopPathForProduct, productGroupLabel } from './productPaths';

import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';

import CatalogBreadcrumb from './CatalogBreadcrumb';

import AddToCartButton from './cart/AddToCartButton';

import { productCharPersonalizationEnabled } from './productPersonalization';
import { getJewelryProfileForProduct, productJewelryCustomizationEnabled } from './jewelry/jewelryCustomization';

import ProductPrice from './catalog/ProductPrice';

import { productEffectivePrice } from './productPricing';

import {

  hrefForSameSiteAsset,

  productGalleryImagePaths,

  productHasVideo,

  primaryVideoSource,

  videoPosterSrc,

} from './productMediaUtils';

import VideoModal from './VideoModal';

import ImageModal from './ImageModal';

import { isOutOfStock, normalizeStockQty } from './cart/cartUtils';

import WishlistHeartButton from './wishlist/WishlistHeartButton';

import ProductReviews from './product/ProductReviews';

import ProductRefId from './catalog/ProductRefId';

import ProductCard from './components/ProductCard';

import PdpDetailAccordions from './components/PdpDetailAccordions';
import { productPdpReturnsNote, productPdpShippingNote } from './productPdpCopy';

import { ReviewStars, useProductReviewSummary } from './product/reviewDisplay';

import ChevronIcon from './components/icons/ChevronIcon';
import { formatPdpSeo } from './siteBrand';



function ShareIcon() {

  return (

    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>

      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />

      <polyline points="16 6 12 2 8 6" />

      <line x1="12" y1="2" x2="12" y2="15" />

    </svg>

  );

}



function WhatsAppIcon() {

  return (

    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden>

      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />

      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .611.611l4.458-1.495A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.37l-.357-.212-2.642.885.885-2.642-.212-.357A9.818 9.818 0 1 1 12 21.818z" />

    </svg>

  );

}



export default function ProductDetailPage() {

  const { categorySlug, productId } = useParams();

  const navigate = useNavigate();

  const { products, categories, loading } = useProducts();

  const { settings: site } = useSiteSettings();

  const purchaseRef = useRef(null);

  const [imageIndex, setImageIndex] = useState(0);

  const [imageModal, setImageModal] = useState({ show: false, image: '', alt: '' });

  const [videoModal, setVideoModal] = useState({ show: false, video: '', alt: '' });

  const [alertEmail, setAlertEmail] = useState('');

  const [alertMsg, setAlertMsg] = useState('');

  const [showStickyBar, setShowStickyBar] = useState(false);



  const product = useMemo(

    () => findProductInCatalog(products, categorySlug, productId),

    [products, categorySlug, productId]

  );



  const catMeta = useMemo(

    () => categories.find((c) => c.slug === categorySlug),

    [categories, categorySlug]

  );



  const reviewSummary = useProductReviewSummary(categorySlug, product?.id || productId);



  const galleryPaths = useMemo(

    () => (product ? productGalleryImagePaths(product) : []),

    [product]

  );



  const related = useMemo(() => {

    if (!product) return [];

    const list = products[categorySlug] || [];

    const g = productGroupKey(product);

    return list

      .filter(

        (p) =>

          String(p.id) !== String(product.id) &&

          (String(p.type || '') === g || String(p.subcategory || '') === g)

      )

      .slice(0, 8);

  }, [product, products, categorySlug]);



  useEffect(() => {

    window.scrollTo(0, 0);

    setImageIndex(0);

  }, [categorySlug, productId]);



  useEffect(() => {

    if (!loading && !product) {

      navigate('/404', { replace: true });

    }

  }, [loading, product, navigate]);



  useEffect(() => {

    const node = purchaseRef.current;

    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(

      ([entry]) => setShowStickyBar(!entry.isIntersecting),

      { threshold: 0, rootMargin: '0px 0px -40px 0px' }

    );

    observer.observe(node);

    return () => observer.disconnect();

  }, [product?.id]);



  if (loading || !product) {

    return (

      <div className="store-page store-page--loading py-5 text-center">

        <div className="spinner-border text-secondary" role="status" />

      </div>

    );

  }



  const title = formatProductTitle(product.name);

  const groupKey = productGroupKey(product);

  const groupPath = shopPathForProduct(product);

  const categoryLabel =

    catMeta?.name || formatDisplayTitle(String(categorySlug || '').replace(/-/g, ' '));

  const groupLabel = productGroupLabel(catMeta, groupKey, categorySlug);

  const categoryHubPath = shopCategoryHubPath(categorySlug);

  const showGroupCrumb = groupKey && groupKey !== 'all';

  const stockQty = normalizeStockQty(product.stock_qty);

  const outOfStock = isOutOfStock(stockQty);

  const price = productEffectivePrice(product);

  const hasVideo = productHasVideo(product);

  const videoSrc = primaryVideoSource(product);

  const mainImage = galleryPaths[imageIndex] || galleryPaths[0] || '';

  const hasMultipleImages = galleryPaths.length > 1;

  const store = site.store_copy || {};

  const pdpBullets = store.pdp_bullets || [];

  const needsOptions =
    productCharPersonalizationEnabled(product) ||
    productJewelryCustomizationEnabled(product) ||
    product.type === 'personalized';

  const trustLine = [store.trust_badge_1, store.trust_badge_2].filter(Boolean).join(' · ');

  const whatsappTemplate = store.whatsapp_product_template || "Hi, I'm interested in {title} (ID: {id}) on Saloni Designs.";

  const whatsappText = whatsappTemplate

    .replace(/\{title\}/g, title)

    .replace(/\{id\}/g, String(product.id));

  const whatsappHref = `${site.whatsapp_href || 'https://wa.me/919680384332'}?text=${encodeURIComponent(whatsappText)}`;

  const pdpSeo = formatPdpSeo(site, {
    title,
    categoryLabel,
    description: product.description,
  });



  const goGallery = (direction) => {

    if (!hasMultipleImages) return;

    setImageIndex((prev) => {

      if (direction === 'next') return (prev + 1) % galleryPaths.length;

      return prev === 0 ? galleryPaths.length - 1 : prev - 1;

    });

  };



  const openImageLightbox = () => {

    if (!mainImage) return;

    setImageModal({ show: true, image: hrefForSameSiteAsset(mainImage), alt: title });

  };



  const scrollToPurchase = () => {

    purchaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  };



  const onShare = () => {

    const url = window.location.href;

    if (navigator.share) void navigator.share({ title, url });

    else {

      void navigator.clipboard.writeText(url);

      window.alert('Link copied.');

    }

  };



  const thumbStrip = hasMultipleImages ? (

    <div className="pdp-gallery__thumbs pdp-gallery__thumbs--strip">

      {galleryPaths.map((path, idx) => (

        <button

          key={path}

          type="button"

          className={`pdp-gallery__thumb${idx === imageIndex ? ' pdp-gallery__thumb--active' : ''}`}

          onClick={() => setImageIndex(idx)}

          aria-label={`View image ${idx + 1}`}

          aria-current={idx === imageIndex ? 'true' : undefined}

        >

          <img src={hrefForSameSiteAsset(path)} alt="" />

        </button>

      ))}

    </div>

  ) : null;



  const galleryStage = (

    <div className="pdp-gallery__stage">

      {hasMultipleImages ? (

        <div className="pdp-gallery__nav-layer" onClick={(e) => e.stopPropagation()} role="presentation">

          <button

            type="button"

            className="pdp-gallery__nav-btn pdp-gallery__nav-btn--side pdp-gallery__nav-btn--prev"

            onClick={() => goGallery('prev')}

            aria-label="Previous image"

          >

            <ChevronIcon direction="left" />

          </button>

          <button

            type="button"

            className="pdp-gallery__nav-btn pdp-gallery__nav-btn--side pdp-gallery__nav-btn--next"

            onClick={() => goGallery('next')}

            aria-label="Next image"

          >

            <ChevronIcon direction="right" />

          </button>

          <span className="pdp-gallery__counter" aria-live="polite">

            {imageIndex + 1} / {galleryPaths.length}

          </span>

        </div>

      ) : null}

      <button

        type="button"

        className="pdp-gallery__main border-0 p-0 bg-transparent w-100"

        onClick={() => {

          if (hasVideo && videoSrc) {

            setVideoModal({ show: true, video: videoSrc, alt: title, noSound: true });

          } else {

            openImageLightbox();

          }

        }}

      >

        {hasVideo && videoSrc ? (

          <div className="pdp-gallery__video-wrap">

            <img src={videoPosterSrc(product)} alt={title} className="pdp-gallery__img" />

            <span className="pdp-gallery__play" aria-hidden>

              ▶

            </span>

          </div>

        ) : (

          <img src={hrefForSameSiteAsset(mainImage)} alt={title} className="pdp-gallery__img" />

        )}

      </button>

    </div>

  );



  return (

    <div className="store-page product-detail-page">

      <Helmet>

        <title>{pdpSeo.title}</title>

        <meta name="description" content={pdpSeo.description} />

      </Helmet>



      <div className="container store-page__container py-4 py-lg-5">

        <CatalogBreadcrumb

          align="start"

          className="catalog-breadcrumb--refined"

          items={[

            { to: '/shop', label: 'Shop' },

            { to: categoryHubPath, label: categoryLabel },

            ...(showGroupCrumb ? [{ to: groupPath, label: groupLabel }] : []),

            { label: title },

          ]}

        />



        <div className="row g-4 g-lg-5">

          <div className="col-lg-6">

            <div className="pdp-gallery pdp-gallery--premium">

              {hasMultipleImages ? (

                <div className="pdp-gallery__layout">

                  {thumbStrip}

                  {galleryStage}

                </div>

              ) : (

                galleryStage

              )}

            </div>

          </div>



          <div className="col-lg-6">

            <div className="pdp-buybox">

              {showGroupCrumb ? (

                <p className="pdp-buybox__kicker">

                  {categoryLabel} · {groupLabel}

                </p>

              ) : (

                <p className="pdp-buybox__kicker">{categoryLabel}</p>

              )}



              <h1 className="pdp-title store-page__title">{title}</h1>



              {!reviewSummary.loading && reviewSummary.count > 0 ? (

                <a href="#product-reviews" className="pdp-review-summary">

                  <ReviewStars rating={Math.round(reviewSummary.average)} size="sm" hideLabel />

                  <span className="pdp-review-summary__score">{reviewSummary.average.toFixed(1)}</span>

                  <span className="text-muted">

                    ({reviewSummary.count} review{reviewSummary.count === 1 ? '' : 's'})

                  </span>

                </a>

              ) : null}



              <ProductRefId id={product.id} className="product-id pdp-product-id" />



              {productJewelryCustomizationEnabled(product) ? (
                <span className="badge rounded-pill mb-2 product-detail-custom-badge">
                  ✦ {getJewelryProfileForProduct(product)?.label || 'Customizable'}
                </span>
              ) : productCharPersonalizationEnabled(product) ? (

                <span className="badge rounded-pill mb-2 product-detail-monogram-badge">

                  ✦ Monogram — pick your letter

                </span>

              ) : product.type === 'personalized' ? (

                <span className="badge rounded-pill text-bg-secondary mb-2">Personalized</span>

              ) : null}



              <p className="pdp-price mb-0">

                <ProductPrice product={product} showBadge />

              </p>



              {trustLine ? <p className="pdp-trust-line">{trustLine}</p> : null}



              {outOfStock ? (

                <p className="text-danger small fw-semibold mb-0">Out of stock</p>

              ) : stockQty !== null ? (

                <p className="small text-muted mb-0">{stockQty} available</p>

              ) : null}



              <div id="pdp-purchase" ref={purchaseRef} className="pdp-purchase">

                {price > 0 && !outOfStock ? (

                  <AddToCartButton product={product} categorySlug={categorySlug} className="pdp-purchase__atc" />

                ) : null}

              </div>



              <div className="pdp-secondary-actions">

                <span className="pdp-icon-action pdp-icon-action--icon-only" title="Save to wishlist">

                  <WishlistHeartButton product={product} categorySlug={categorySlug} size="sm" />

                </span>

                <button type="button" className="pdp-icon-action" onClick={onShare} aria-label="Share product">

                  <ShareIcon />

                  <span>Share</span>

                </button>

                <a

                  href={whatsappHref}

                  className="pdp-icon-action"

                  target="_blank"

                  rel="noopener noreferrer"

                >

                  <WhatsAppIcon />

                  <span>Ask</span>

                </a>

              </div>



              <PdpDetailAccordions
                aboutHeading={store.pdp_about_heading}
                description={product.description}
                bullets={pdpBullets}
                shippingHeading={store.pdp_shipping_heading || 'Shipping & delivery'}
                shippingNote={productPdpShippingNote(product, store)}
                returnsHeading={store.pdp_returns_heading || 'Returns & care'}
                returnsNote={productPdpReturnsNote(product, store)}
              />



              {outOfStock ? (

                <form
                  className="pdp-notify-stock mt-3"
                  onSubmit={async (e) => {

                    e.preventDefault();

                    setAlertMsg('');

                    try {

                      const r = await fetch('/api/customer.php', {

                        method: 'POST',

                        headers: { 'Content-Type': 'application/json' },

                        body: JSON.stringify({

                          action: 'stock_alert',

                          email: alertEmail.trim(),

                          category_slug: categorySlug,

                          product_id: product.id,

                        }),

                      });

                      const d = await r.json();

                      setAlertMsg(d.message || d.error || (d.success ? 'Subscribed.' : 'Could not subscribe.'));

                    } catch {

                      setAlertMsg('Could not subscribe.');

                    }

                  }}

                >

                  <p className="small fw-semibold mb-2">Notify me when back in stock</p>

                  <div className="input-group input-group-sm">

                    <input

                      type="email"

                      className="form-control"

                      placeholder="Your email"

                      value={alertEmail}

                      onChange={(e) => setAlertEmail(e.target.value)}

                      required

                    />

                    <button type="submit" className="btn btn-primary">

                      Notify

                    </button>

                  </div>

                  {alertMsg ? <p className="small mt-2 mb-0">{alertMsg}</p> : null}

                </form>

              ) : null}



              {price <= 0 ? (

                <p className="small text-muted mt-2 mb-0">

                  This design may need a custom quote — message us on WhatsApp with the product name.

                </p>

              ) : null}

            </div>

          </div>

        </div>



        <ProductReviews categorySlug={categorySlug} productId={product.id} />



        {related.length > 0 ? (

          <section className="pdp-related mt-5 pt-4 border-top">

            <h2 className="pdp-section-title">More in this collection</h2>

            <div className="pdp-related-scroll">

              {related.map((p) => (

                <ProductCard key={p.id} product={p} categorySlug={categorySlug} showRefId={false} />

              ))}

            </div>

          </section>

        ) : null}

      </div>



      {showStickyBar && price > 0 && !outOfStock ? (

        <div className="pdp-sticky-bar d-lg-none" role="region" aria-label="Quick add to bag">

          <p className="pdp-sticky-bar__price mb-0">

            <ProductPrice product={product} showBadge={false} />

          </p>

          {needsOptions ? (

            <button type="button" className="btn btn-primary pdp-sticky-bar__scroll-btn" onClick={scrollToPurchase}>

              Choose options

            </button>

          ) : (

            <AddToCartButton

              product={product}

              categorySlug={categorySlug}

              variant="inline"

              compact

              className="pdp-sticky-bar__atc"

            />

          )}

        </div>

      ) : null}



      <ImageModal

        show={imageModal.show}

        image={

          imageModal.show && galleryPaths[imageIndex]

            ? hrefForSameSiteAsset(galleryPaths[imageIndex])

            : imageModal.image

        }

        alt={imageModal.alt}

        onClose={() => setImageModal({ show: false, image: '', alt: '' })}

        onNext={() => goGallery('next')}

        onPrev={() => goGallery('prev')}

        hasMultipleImages={hasMultipleImages}

        currentIndex={imageIndex}

        totalImages={galleryPaths.length}

      />

      <VideoModal

        show={videoModal.show}

        video={videoModal.video}

        alt={videoModal.alt}

        noSound={videoModal.noSound}

        onClose={() => setVideoModal({ show: false, video: '', alt: '' })}

      />

    </div>

  );

}


